import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root',
})
export class MunicipalGsacPricingService {
  constructor() {}

  commonFields = `
        MIC
        LocalCode
        ISIN
        Currency
        PriceDate
        Open
        High
        Low
        TWAP
        CumulativeAdjustmentFactor
        PreviousClose
        Close
        Mid
        Ask
        Last
        LastMarket
        LastSize
        LastTimestamp
        QuoteTimestamp
        Bid
        Bidsize
        BidMarket
        Asksize
        AskMarket
        BidModifiedDuration
        BidConvexity
        AccruedInterest        
        BidYTW
        MidYTW
        AskYTW
        LiquidityScore
        NumberOfTrades
        TradedVolume
        VWAP
        TradingStatus
        Change
        ChangePercentage
        Feed
        IsDelayed
        DelayedInterval
        SecurityID
        MktCloseDate
        VolFlag
        IssuerName
        SecurityType
        SecurityTypeDescription
        SEDOL
        CUSIP
        PrimaryExchange
        Exchange
        TradedValue
        TotalTrades
        CloseType
        YieldOfMaturity
        Type
        ProductType
        TypeCode
        BondMnemonic
        BondType
        CloseDate
        Broker
        Note
        AssetClass
        AdjustedPrice
        FXConvertedClosePrice
        FXConvertedCurrency
        FXConvertedRate
        FXConvertedPrice
  `;
  // The following fields are only available for the Municipal Pricing service
  municipalFields = `
    PriceToDateBid
    PriceToBid
    PriceToDateMid
    PriceToMid
    PriceToDateAsk
    PriceToAsk
    EvaluatedBidAskPriceSpread
    EvaluatedBidAskYieldSpread
    TradeData
    LastTradeDate
    TradeVolume1Day
    TradeVolume10Day
    TradeVolume30Day
    TradeVolume60Day
    TradeVolume90Day
    TradeCount1Day
    TradeCount10Day
    TradeCount30Day
    TradeCount60Day
    TradeCount90Day
    QuotesDealerCount1Day
    QuotesDealerCount10Day
    QuotesDealerCount30Day
    QuotesDealerCount60Day
    QuotesDealerCount90Day
    BidEffectiveDurationToWorst
    MidEffectiveDurationToWorst
    AskEffectiveDurationToWorst
    BidYieldToParCall
  `;

  // Fields that live on BOTH MunicipalPricing and CorporatePricing. They are
  // already declared under a primary category above (e.g. BidOAS in gsacFields);
  // this list makes filterFields also surface them in the Municipal fragment so
  // the field is queryable for both asset types without regressing either.
  municipalCorporateSharedFields = `
    BidOAS
    BidYieldToNextCall
  `;

  securitizedFields = `
    MidModifiedDuration
    MidYTM
    BidYTM
    BidYTC
    MidYTC
    BidDiscountMargin
    AskDiscountMargin
    MidOAS
    EffectiveDurationToCall
    EffectiveDurationToMaturity
    OAS
    CurrentWALToCall
    CurrentWALToMaturity
    CurrentWALToWorst
    CurrentYieldToCall
    CurrentYieldToMaturity
    CurrentYieldToWorst
    TradeVolume1Day
    TradeVolume10Day
    TradeVolume30Day
    TradeVolume60Day
    TradeVolume90Day
    TradeCount1Day
    TradeCount10Day
    TradeCount30Day
    TradeCount60Day
    TradeCount90Day
    QuotesDealerCount1Day
    QuotesDealerCount10Day
    QuotesDealerCount30Day
    QuotesDealerCount60Day
    QuotesDealerCount90Day
  `;

  // The following fields are only available for the GSAC Pricing service
  gsacFields = `
    BidAskPriceSpread
    DirtyBidPrice
    DirtyMidPrice
    DirtyAskPrice
    BidYTM
    MidYTM
    AskYTM
    BidAskYieldSpread
    YTWDate
    BidSTW
    BidDiscountMargin
    MidDiscountMargin
    AskDiscountMargin
    BidMacaulayDuration
    MidMacaulayDuration
    AskMacaulayDuration
    MidModifiedDuration
    AskModifiedDuration
    MidConvexity
    AskConvexity
    BidOAS
    MidOAS
    AskOAS
    BidEffectiveDuration
    MidEffectiveDuration
    AskEffectiveDuration
    BidEffectiveConvexity
    MidEffectiveConvexity
    AskEffectiveConvexity
    BondAgencyData
    QuoteData
    TraceData
    InterdealerBrokerData
    LiquidityASOF
    BidGSpread
    MidGSpread
    AskGSpread
    BidZSpread
    MidZSpread
    AskZSpread
    TradeVolume1Day
    TradeVolume10Day
    TradeVolume30Day
    TradeVolume60Day
    TradeVolume90Day
    TradeCount1Day
    TradeCount10Day
    TradeCount30Day
    TradeCount60Day
    TradeCount90Day
    QuotesDealerCount1Day
    QuotesDealerCount10Day
    BidEffectiveDurationToNextCall
    BidMacaulayDurationToNextCall
    BidModifiedDurationToNextCall
    BidYieldToNextCall
  `;
  // The following fields are only available for the option Pricing service
  optionpricingFields = `
    UnderlyingOptionClosePrice
    UnderlyingOptionClosePriceDate
  `;

  filterFields(priceData: string[]) {
    // Convert the multiline strings into arrays
    const commonFieldsArray = this.commonFields.trim().split(/\s+/);
    const municipalFieldsArray = this.municipalFields.trim().split(/\s+/);
    const municipalCorporateSharedFieldsArray =
      this.municipalCorporateSharedFields.trim().split(/\s+/);
    const securitizedFieldsArray = this.securitizedFields.trim().split(/\s+/);
    const gsacFieldsArray = this.gsacFields.trim().split(/\s+/);
    const optionPricingFieldsArray = this.optionpricingFields
      .trim()
      .split(/\s+/);
    // Initialize the result arrays
    const municipalFieldsSelected: string[] = [];
    const gsacFieldsSelected: string[] = [];
    const securitizedFieldsSelected: string[] = [];
    const pricingFields: string[] = [];
    const optionPricingFieldsSelected: string[] = [];

    // Iterate through the priceData array and categorize fields
    priceData.forEach((field) => {
      if (commonFieldsArray.includes(field)) {
        // If it's a common field, include in all arrays
        municipalFieldsSelected.push(field);
        securitizedFieldsSelected.push(field);
        gsacFieldsSelected.push(field);
        optionPricingFieldsSelected.push(field);
        pricingFields.push(field);
      } else if (municipalFieldsArray.includes(field)) {
        // If it's only a municipal field, include in municipalFieldsSelected
        municipalFieldsSelected.push(field);
      } else if (gsacFieldsArray.includes(field)) {
        // If it's only a gsac field, include in gsacFieldsSelected
        gsacFieldsSelected.push(field);
      } else if (optionPricingFieldsArray.includes(field)) {
        // If it's only a option pricing field, include in optionPricingFieldsSelected
        optionPricingFieldsSelected.push(field);
      } else if (!securitizedFieldsArray.includes(field)) {
        // If it doesn't belong to any category, add to pricingFields.
        // Securitized-only fields (e.g. BidYTC/MidYTC) are excluded here: they
        // exist only on the SecuritizedPricing type, not the PriceBase
        // interface, so they must go in the SecuritizedPricing inline fragment
        // (added below) — querying them at interface level fails validation.
        pricingFields.push(field);
      }

      if (securitizedFieldsArray.includes(field)) {
        // If it's only a securitized field, include in securitizedFieldsSelected
        securitizedFieldsSelected.push(field);
      }

      if (
        municipalCorporateSharedFieldsArray.includes(field) &&
        !municipalFieldsSelected.includes(field)
      ) {
        // Shared municipal/corporate fields (e.g. BidOAS) are categorized under
        // gsacFields by the chain above; also add them to the Municipal fragment
        // so they resolve for municipal instruments too.
        municipalFieldsSelected.push(field);
      }
    });

    return {
      municipalFieldsSelected,
      securitizedFieldsSelected,
      gsacFieldsSelected,
      optionPricingFieldsSelected,
      pricingFields,
    };
  }
}
